/**
 * BoekingenList - Overzicht van alle boekingen
 * Gebaseerd op Morris boekingen.html
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import styles from '../../styles/Boekhouding.module.css';
import { boekhoudingService, Boeking, BoekstukType } from '../../services/boekhouding.service';

type SortField = 'boekstuknummer' | 'datum' | 'omschrijving' | 'grootboeknaam' | 'boekstuk' | 'bedrag_excl' | 'bedrag_incl' | 'btw_bedrag';
type SortOrder = 'asc' | 'desc';

const BoekingenList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [boekingen, setBoekingen] = useState<Boeking[]>([]);

  // Filters uit URL
  const boekstukFilter = searchParams.get('boekstuk') as BoekstukType | null;
  const kwartaalFilter = searchParams.get('kwartaal') ? parseInt(searchParams.get('kwartaal')!) : null;
  const sortField = (searchParams.get('sort') as SortField) || 'datum';
  const sortOrder = (searchParams.get('order') as SortOrder) || 'desc';

  // Huidig boekjaar uit localStorage
  const huidigJaar = parseInt(localStorage.getItem('boekhouding_jaar') || new Date().getFullYear().toString());

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const response = await boekhoudingService.getBoekingen({
          jaar: huidigJaar,
          boekstuk: boekstukFilter || undefined,
          limit: 100,
        });
        let items = response.items;

        // Filter op kwartaal client-side
        if (kwartaalFilter) {
          items = items.filter(b => {
            const maand = new Date(b.datum).getMonth() + 1;
            const bKwartaal = Math.ceil(maand / 3);
            return bKwartaal === kwartaalFilter;
          });
        }

        // Sorteer
        items.sort((a, b) => {
          let aVal: any = a[sortField as keyof Boeking];
          let bVal: any = b[sortField as keyof Boeking];

          // Datum vergelijking
          if (sortField === 'datum') {
            aVal = new Date(aVal).getTime();
            bVal = new Date(bVal).getTime();
          }

          // Nummer vergelijking
          if (['bedrag_excl', 'bedrag_incl', 'btw_bedrag'].includes(sortField)) {
            aVal = Number(aVal) || 0;
            bVal = Number(bVal) || 0;
          }

          // Boekstuknummer: string vergelijking (bijv. I-26001)
          if (sortField === 'boekstuknummer') {
            aVal = String(aVal || '');
            bVal = String(bVal || '');
          }

          if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
          if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
          return 0;
        });

        setBoekingen(items);
      } catch (err) {
        console.error('Error loading boekingen:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [huidigJaar, boekstukFilter, kwartaalFilter, sortField, sortOrder]);

  const formatBedrag = (bedrag: number): string => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number(bedrag) || 0);
  };

  const formatDatum = (datum: string): string => {
    return new Date(datum).toLocaleDateString('nl-NL');
  };

  // Een inkoop- of verkoopboeking die helemaal voldaan is krijgt een groene
  // badge in plaats van de kleur van het boekstuk. Gevraagd door Dylan 25-10-2026.
  const isBetaald = (boeking: Boeking): boolean => {
    if (boeking.boekstuk !== 'inkoop' && boeking.boekstuk !== 'verkoop') return false;
    const totaal = Number(boeking.bedrag_incl) || 0;
    const betaald = Number(boeking.betaald) || 0;
    return totaal > 0 && betaald + 0.005 >= totaal;
  };

  const badgeKlasse = (boeking: Boeking): string => {
    // Alleen in een gefilterde lijst. Bij "Alle" is de badge het enige dat de
    // boekstukken uit elkaar houdt; daar blijft de kleur van het boekstuk staan.
    if (boekstukFilter && isBetaald(boeking)) return styles.badgeBetaald;
    const naam = boeking.boekstuk.charAt(0).toUpperCase() + boeking.boekstuk.slice(1);
    return styles[`badge${naam}`];
  };

  const formatBoekstuknr = (nr: string | number | undefined, _boekstuk: string): string => {
    if (!nr) return '-';
    return String(nr);
  };

  // Filter handlers
  const setFilter = (key: string, value: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params);
  };

  const toggleSort = (field: SortField) => {
    const params = new URLSearchParams(searchParams);
    if (sortField === field) {
      params.set('order', sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      params.set('sort', field);
      params.set('order', 'asc');
    }
    setSearchParams(params);
  };

  // Bereken totalen
  const totaalExcl = boekingen.reduce((sum, b) => sum + (Number(b.bedrag_excl) || 0), 0);
  const totaalBtw = boekingen.reduce((sum, b) => sum + (Number(b.btw_bedrag) || 0), 0);
  const totaalIncl = boekingen.reduce((sum, b) => sum + (Number(b.bedrag_incl) || 0), 0);

  // Voor kas/bank: credit/debet berekening
  const totaalCredit = boekingen.filter(b => b.is_credit).reduce((sum, b) => sum + (Number(b.bedrag_incl) || 0), 0);
  const totaalDebet = boekingen.filter(b => !b.is_credit).reduce((sum, b) => sum + (Number(b.bedrag_incl) || 0), 0);

  // Header actions
  const headerActions = (
    <>
      <Link
        to={`/boekhouding/scan?type=${boekstukFilter === 'verkoop' ? 'verkoop' : 'inkoop'}`}
        className={`${styles.btn} ${styles.btnSuccess}`}
      >
        Scan Factuur
      </Link>
      <Link
        to={`/boekhouding/boeking/nieuw${boekstukFilter ? `?boekstuk=${boekstukFilter}` : ''}`}
        className={`${styles.btn} ${styles.btnPrimary}`}
      >
        + Nieuwe Boeking
      </Link>
    </>
  );

  if (loading) {
    return (
      <BoekhoudingLayout title="Boekingen" headerActions={headerActions}>
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  return (
    <BoekhoudingLayout title="Boekingen" headerActions={headerActions}>
      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label>Boekstuk:</label>
          <div className={styles.filterButtons}>
            <button
              className={`${styles.filterBtn} ${!boekstukFilter ? styles.filterBtnActive : ''}`}
              onClick={() => setFilter('boekstuk', null)}
            >
              Alle
            </button>
            <button
              className={`${styles.filterBtn} ${boekstukFilter === 'kas' ? styles.filterBtnActive : ''}`}
              onClick={() => setFilter('boekstuk', 'kas')}
            >
              Kas
            </button>
            <button
              className={`${styles.filterBtn} ${boekstukFilter === 'bank' ? styles.filterBtnActive : ''}`}
              onClick={() => setFilter('boekstuk', 'bank')}
            >
              Bank
            </button>
            <button
              className={`${styles.filterBtn} ${boekstukFilter === 'inkoop' ? styles.filterBtnActive : ''}`}
              onClick={() => setFilter('boekstuk', 'inkoop')}
            >
              Inkoop
            </button>
            <button
              className={`${styles.filterBtn} ${boekstukFilter === 'verkoop' ? styles.filterBtnActive : ''}`}
              onClick={() => setFilter('boekstuk', 'verkoop')}
            >
              Verkoop
            </button>
          </div>
        </div>
        <div className={styles.filterGroup}>
          <label>Kwartaal:</label>
          <div className={styles.filterButtons}>
            <button
              className={`${styles.filterBtn} ${!kwartaalFilter ? styles.filterBtnActive : ''}`}
              onClick={() => setFilter('kwartaal', null)}
            >
              Alle
            </button>
            {[1, 2, 3, 4].map(q => (
              <button
                key={q}
                className={`${styles.filterBtn} ${kwartaalFilter === q ? styles.filterBtnActive : ''}`}
                onClick={() => setFilter('kwartaal', q.toString())}
              >
                Q{q}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mobiele Card View */}
      <div className={`${styles.boekingenCards} ${styles.mobileOnly}`}>
        {boekingen.length === 0 ? (
          <div className={styles.emptyStateCard}>Geen boekingen gevonden</div>
        ) : (
          <>
            {boekingen.map(boeking => (
              <Link
                key={boeking.id}
                to={`/boekhouding/boeking/${boeking.id}`}
                className={styles.boekingCard}
              >
                <div className={styles.boekingCardHeader}>
                  <span className={`${styles.badge} ${badgeKlasse(boeking)}`} title={boekstukFilter && isBetaald(boeking) ? 'Betaald' : undefined}>
                    {boeking.boekstuk.charAt(0).toUpperCase() + boeking.boekstuk.slice(1)}
                  </span>
                  <span className={styles.boekingDate}>{formatDatum(boeking.datum)}</span>
                </div>
                <div className={styles.boekingCardBody}>
                  <div className={styles.boekingOmschrijving}>{boeking.relatie_naam || boeking.omschrijving || '-'}</div>
                  <div className={styles.boekingGrootboek}>{boeking.grootboeknaam}</div>
                </div>
                <div className={styles.boekingCardFooter}>
                  <span className={styles.boekingBedrag}>{formatBedrag(boeking.bedrag_incl)}</span>
                </div>
              </Link>
            ))}
            <div className={styles.boekingenTotaalCard}>
              <div className={styles.boekingCardFooterRow}>
                <span>Totaal incl. BTW:</span>
                <strong>{formatBedrag(totaalIncl)}</strong>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Desktop Tabel View */}
      <div className={`${styles.card} ${styles.desktopOnly}`}>
        <div className={styles.tableContainer}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th className={styles.sortable} onClick={() => toggleSort('boekstuknummer')}>
                  Bkst.nr. {sortField === 'boekstuknummer' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th className={styles.sortable} onClick={() => toggleSort('datum')}>
                  Datum {sortField === 'datum' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th className={styles.sortable} onClick={() => toggleSort('omschrijving')}>
                  Relatie {sortField === 'omschrijving' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th className={styles.sortable} onClick={() => toggleSort('grootboeknaam')}>
                  Grootboek {sortField === 'grootboeknaam' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                <th className={styles.sortable} onClick={() => toggleSort('boekstuk')}>
                  Boekstuk {sortField === 'boekstuk' && (sortOrder === 'asc' ? '▲' : '▼')}
                </th>
                {boekstukFilter === 'kas' || boekstukFilter === 'bank' ? (
                  <>
                    <th className={`${styles.sortable} ${styles.alignRight}`}>Credit</th>
                    <th className={`${styles.sortable} ${styles.alignRight}`}>Debet</th>
                  </>
                ) : (
                  <>
                    <th className={`${styles.sortable} ${styles.alignRight}`} onClick={() => toggleSort('bedrag_excl')}>
                      Excl. BTW {sortField === 'bedrag_excl' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className={`${styles.sortable} ${styles.alignRight}`} onClick={() => toggleSort('btw_bedrag')}>
                      BTW {sortField === 'btw_bedrag' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </th>
                    <th className={`${styles.sortable} ${styles.alignRight}`} onClick={() => toggleSort('bedrag_incl')}>
                      Incl. BTW {sortField === 'bedrag_incl' && (sortOrder === 'asc' ? '▲' : '▼')}
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {boekingen.length === 0 ? (
                <tr>
                  <td colSpan={8} className={styles.emptyState}>Geen boekingen gevonden</td>
                </tr>
              ) : (
                boekingen.map(boeking => (
                  <tr
                    key={boeking.id}
                    className={styles.clickable}
                    onDoubleClick={() => window.location.href = `/boekhouding/boeking/${boeking.id}`}
                    title="Dubbelklik om te bewerken"
                  >
                    <td>{formatBoekstuknr(boeking.boekstuknummer, boeking.boekstuk)}</td>
                    <td>{formatDatum(boeking.datum)}</td>
                    <td>{boeking.relatie_naam || boeking.omschrijving || '-'}</td>
                    <td>{boeking.grootboeknaam}</td>
                    <td>
                      <span className={`${styles.badge} ${badgeKlasse(boeking)}`} title={boekstukFilter && isBetaald(boeking) ? 'Betaald' : undefined}>
                        {boeking.boekstuk.charAt(0).toUpperCase() + boeking.boekstuk.slice(1)}
                      </span>
                    </td>
                    {boekstukFilter === 'kas' || boekstukFilter === 'bank' ? (
                      <>
                        <td className={styles.alignRight}>
                          {boeking.is_credit ? formatBedrag(boeking.bedrag_incl) : <span className={styles.textMuted}>-</span>}
                        </td>
                        <td className={styles.alignRight}>
                          {!boeking.is_credit ? formatBedrag(boeking.bedrag_incl) : <span className={styles.textMuted}>-</span>}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className={styles.alignRight}>{formatBedrag(boeking.bedrag_excl)}</td>
                        <td className={styles.alignRight}>{formatBedrag(boeking.btw_bedrag)}</td>
                        <td className={styles.alignRight}>{formatBedrag(boeking.bedrag_incl)}</td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
            {boekingen.length > 0 && (
              <tfoot>
                {boekstukFilter === 'kas' || boekstukFilter === 'bank' ? (
                  <>
                    <tr>
                      <td colSpan={5}></td>
                      <td className={styles.alignRight}><strong>========</strong></td>
                      <td className={styles.alignRight}><strong>========</strong></td>
                    </tr>
                    <tr>
                      <td colSpan={5}></td>
                      <td className={styles.alignRight}><strong>{formatBedrag(totaalCredit)}</strong></td>
                      <td className={styles.alignRight}><strong>{formatBedrag(totaalDebet)}</strong></td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={5}><strong>Totaal</strong></td>
                    <td className={styles.alignRight}><strong>{formatBedrag(totaalExcl)}</strong></td>
                    <td className={styles.alignRight}><strong>{formatBedrag(totaalBtw)}</strong></td>
                    <td className={styles.alignRight}><strong>{formatBedrag(totaalIncl)}</strong></td>
                  </tr>
                )}
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </BoekhoudingLayout>
  );
};

export default BoekingenList;
